import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const LabContext = createContext(null);

const PLATFORM_DOMAIN = 'xpiva.com';

/* DID A PERSON CHOOSE THIS TENANT, OR DID WE GUESS IT?
 *
 * `selectedCompanyId` alone cannot answer that, and the difference decides
 * who wins when the domain says one thing and a stored value says another.
 * An explicit pick must survive — a platform user who deliberately switched
 * to another tenant should stay there. A GUESS must not: it is exactly what
 * the domain is there to correct.
 *
 * Written only by `selectCompany`, which is the switcher. Everything else
 * that sets a company id is guessing. */
const EXPLICIT_KEY = 'selectedCompanyIdExplicit';
const pickWasExplicit = () => {
  try {
    return localStorage.getItem(EXPLICIT_KEY)
        === (localStorage.getItem('selectedCompanyId') || '');
  } catch { return false; }
};

/* A HOST THAT NAMES A TENANT: a subdomain of the platform, or a customer's
 * own domain. Known synchronously from the URL, which is what makes it
 * usable as a gate — unlike `tenantLocked`, which arrives from the network
 * and is therefore racy. One definition, used by both effects below. */
function isTenantHost() {
  const host = window.location.hostname.toLowerCase();
  return host !== PLATFORM_DOMAIN && host !== `www.${PLATFORM_DOMAIN}`
      && host !== 'localhost'
      && (host.endsWith(`.${PLATFORM_DOMAIN}`) || !host.match(/^\d/));
}

export function LabProvider({ children, currentUser }) {
  const [parentCompanies, setParentCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(() => localStorage.getItem('selectedCompanyId') || '');
  const [selectedLabId, setSelectedLabId] = useState(() => {
    const stored = localStorage.getItem('selectedLabId') || '';
    // The '__all__' sentinel means "user explicitly chose system-wide view".
    // Surface it to the rest of the app as '' (no header sent), but the
    // auto-pick logic checks the raw localStorage value to know not to re-pin.
    return stored === '__all__' ? '' : stored;
  });
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCorporateUser, setIsCorporateUser] = useState(false);
  const [tenantLocked, setTenantLocked] = useState(false); // true when on a subdomain — can't switch

  // Detect subdomain and pre-select that tenant. For regular users we
  // also set `tenantLocked = true` so they can't switch away. Platform
  // users (superadmin / is_global / is_master) are given the subdomain
  // as a DEFAULT selection only — the carve-out in the isCorporateUser
  // effect above keeps the switcher visible for them.
  useEffect(() => {
    if (!isTenantHost()) return;

    api.get('/api/v1/domains/current').then(res => {
      const company = res.data?.company;
      if (!company?.parent_company_id) return;
      // THE DOMAIN BEATS A GUESS, AND LOSES ONLY TO A PERSON.
      //
      //   "i entered order 950, then i saw it for a few seconds, i selected
      //    it then i dissapeared? in prod"
      //
      // This used to be `if (!stored)` — fill the blank, never correct a
      // value. But `fetchCompanies` below auto-picks companies[0] whenever
      // nothing is selected yet, and the two run as a RACE: `tenantLocked`
      // starts false, so if the company list answers first it writes
      // companies[0] into localStorage, and this effect then finds a
      // `stored` value and leaves it alone. On brioclinical.com — which
      // resolves to Brio — a platform user with five tenants got pinned to
      // whichever sorts first; the Orders list went empty and STAYED empty,
      // because the wrong answer persisted in localStorage.
      //
      // That is also why the order flickered: the first /orders request went
      // out before any company was selected, so it carried no X-Company-Id
      // and the SERVER resolved the tenant from the domain — correctly. The
      // next request carried the header and asked a tenant with none of
      // these orders in it.
      const stored = localStorage.getItem('selectedCompanyId');
      if (!stored || !pickWasExplicit()) {
        setSelectedCompanyId(company.parent_company_id);
        try {
          localStorage.setItem('selectedCompanyId', company.parent_company_id);
          // Not an explicit pick: the switcher can still move away from it.
          localStorage.removeItem(EXPLICIT_KEY);
        } catch { /* private mode */ }
      }
      setTenantLocked(true);  // carve-out for platform users lives in the next effect
    }).catch(() => {});
  }, []);

  // Determine if user has corporate access (can switch companies).
  //
  // Three tiers, in order:
  //   1. Platform users (superadmin / is_global / is_master) — always get
  //      the switcher regardless of subdomain. Carve-out for staff who
  //      need cross-tenant access from any URL.
  //   2. Multi-company users — anyone with access to MORE THAN ONE company
  //      via user_companies. The /api/v1/saas/companies endpoint already
  //      tenant-scopes this for them. If they have multiple, show the
  //      switcher even on a subdomain — otherwise they're permanently
  //      pinned to whichever tenant the subdomain happens to resolve to
  //      and can never get to the others without a SQL flag flip.
  //   3. Single-company admin/corporate users on the root domain — show
  //      the switcher (legacy behavior, mainly useful for tenant-pick on
  //      first login before the subdomain pins them).
  //
  // Subdomain tenant-lock remains a UX hint — it auto-selects the right
  // company when a user lands on, say, brio.xpiva.com — but it no longer
  // hides the switcher from users who legitimately have multi-tenant
  // access via user_companies.
  useEffect(() => {
    const role = (currentUser?.role || '').toLowerCase();
    const isPlatformLevel =
      role === 'superadmin' ||
      !!currentUser?.is_global ||
      !!currentUser?.is_master;
    const corporate = ['superadmin', 'admin', 'corporate', 'master'].includes(role);
    const hasMultiCompanyAccess = parentCompanies.length > 1;
    setIsCorporateUser(
      isPlatformLevel ||
      (corporate && (!tenantLocked || hasMultiCompanyAccess))
    );
  }, [currentUser, tenantLocked, parentCompanies.length]);

  // Fetch parent companies the user has access to
  const fetchCompanies = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/saas/companies');
      const allCompanies = Array.isArray(res.data) ? res.data : [];
      // Filter out deactivated/suspended companies from the selector
      const companies = allCompanies.filter(c => (c.status || 'active').toLowerCase() === 'active');
      setParentCompanies(companies);

      // AND NEVER GUESS ON A TENANT HOST.
      //
      // `tenantLocked` is not a safe test here: it is set by the domain
      // lookup, which is still in flight when this resolves first — that
      // race is the bug above. The HOSTNAME is known synchronously and
      // cannot be raced, so it is what gates the guess. On the platform
      // domain there is no domain answer coming and companies[0] is the
      // only sensible default, exactly as before.
      if (!selectedCompanyId && companies.length > 0
          && !tenantLocked && !isTenantHost()) {
        const firstId = companies[0].parent_company_id;
        setSelectedCompanyId(firstId);
        localStorage.setItem('selectedCompanyId', firstId);
      }
    } catch (err) {
      console.warn('Failed to load companies:', err);
      setParentCompanies([]);
    }
  }, [selectedCompanyId, tenantLocked]);

  // Fetch labs — superadmin sees all, others see only assigned labs
  const fetchLabs = useCallback(async () => {
    if (!selectedCompanyId) { setLabs([]); return; }
    try {
      const role = (currentUser?.role || '').toLowerCase();
      let labList = [];

      // Field-staff roles work *at* labs (they have a `technicians` row);
      // admins/managers SUPERVISE labs (they have `user_labs` access).
      // For field staff the picker should be the intersection — they
      // shouldn't see labs in their company where they aren't actually
      // a registered technician.
      const FIELD_STAFF = ['phlebotomist', 'driver', 'drivers', 'tech',
        'technician', 'lab_technician', 'warehouse', 'client_service',
        'dispatcher', 'accessioner'];

      if (role === 'superadmin') {
        // Superadmin sees all labs for the company
        const res = await api.get(`/api/v1/saas/companies/${selectedCompanyId}/labs`);
        labList = Array.isArray(res.data) ? res.data : [];
      } else if (FIELD_STAFF.includes(role)) {
        // Filter to labs where they actually have a tech row
        let myLabs = [];
        try {
          const t = await api.get('/api/technicians/me');
          myLabs = (Array.isArray(t.data) ? t.data : []).map(x => x.lab_id).filter(Boolean);
        } catch { /* fall through */ }
        try {
          const ctx = await api.get('/api/v1/saas/my-context');
          const allAssigned = (ctx.data?.assigned_labs || []).map(l => ({ ...l, lab_id: l.lab_id }));
          // If they have any technicians rows, intersect; otherwise keep
          // the wider user_labs assignment so they don't get stranded.
          labList = myLabs.length > 0
            ? allAssigned.filter(l => myLabs.includes(l.lab_id))
            : allAssigned;
        } catch {
          const res = await api.get(`/api/v1/saas/companies/${selectedCompanyId}/labs`);
          const all = Array.isArray(res.data) ? res.data : [];
          labList = myLabs.length > 0 ? all.filter(l => myLabs.includes(l.lab_id)) : all;
        }
      } else {
        // Admins / managers / others see all labs they have user_labs access
        // to — BUT scoped to the currently-selected company. /my-context
        // returns the user's full lab access across all companies; we filter
        // to just this company's labs so switching companies actually swaps
        // the lab list. Without this filter, multi-tenant admins see every
        // lab they have access to under every company they belong to.
        try {
          const ctx = await api.get('/api/v1/saas/my-context');
          const all = (ctx.data?.assigned_labs || []).map(l => ({ ...l, lab_id: l.lab_id }));
          labList = all.filter(l =>
            !l.parent_company_id || l.parent_company_id === selectedCompanyId
          );
        } catch {
          const res = await api.get(`/api/v1/saas/companies/${selectedCompanyId}/labs`);
          labList = Array.isArray(res.data) ? res.data : [];
        }
      }

      // Nobody assigned this login a laboratory.
      //
      // `assigned_labs` is built purely from `user_laboratories`, and an
      // external client practice never gets a row there — they order INTO a
      // lab, they don't work at one. The result was an empty list, which is
      // not a harmless one: every "Save to Lab *" gate in the app reads this
      // array, so New Requisition and Add New Patient showed a REQUIRED
      // dropdown with nothing in it and no way forward.
      //
      // Fall back to the laboratories the server says this session's own
      // company has. `/api/identity/my-context` answers for the caller's own
      // account and is the one context endpoint a client login is allowed to
      // call — it is already the header's fallback for the same reason.
      if (labList.length === 0) {
        try {
          const idc = await api.get('/api/identity/my-context');
          const fromCompany = idc.data?.laboratories || [];
          const idCompany = String(idc.data?.company?.parent_company_id || '');
          // Whose company is this route answering about?
          //
          // It always answers about the CALLER's own. For a multi-company
          // admin who has switched companies that would list the wrong
          // laboratories under the right name, so they only get the fallback
          // when it agrees with the company on screen.
          //
          // Everyone else has exactly one company and cannot switch. For them
          // this route is the authority and `selectedCompanyId` is not: it
          // lives in localStorage and survives from whatever was last viewed
          // in that browser, so a stale or empty value was making the labs
          // disappear on a session that had no way to fix it. Adopt the
          // server's answer and correct the stored one.
          const canSwitch = parentCompanies.length > 1 ||
            ['superadmin', 'admin', 'corporate', 'master']
              .includes((currentUser?.role || '').toLowerCase());
          const trusted = idCompany && (!canSwitch || idCompany === String(selectedCompanyId));
          if (trusted && fromCompany.length > 0) {
            labList = fromCompany.map(l => ({ ...l, lab_id: String(l.lab_id) }));
            if (idCompany !== String(selectedCompanyId)) {
              try { localStorage.setItem('selectedCompanyId', idCompany); } catch { /* private mode */ }
            }
          }
        } catch { /* leave the list empty — the screens still say so */ }
      }

      // Filter out deactivated labs from the selector
      labList = labList.filter(l => l.is_active !== false);

      setLabs(labList);

      // Auto-select first lab ONLY on first visit. Honour the '__all__'
      // sentinel for admin/superadmin (they need a way to view system-wide).
      // For tech/client/patient, ALWAYS auto-pin to a lab — even if
      // '__all__' is in localStorage (someone could set it manually).
      if (labList.length > 0) {
        const stored = localStorage.getItem('selectedLabId');
        const role = currentUser?.role;
        const canEscape = role === 'admin' || role === 'superadmin';
        const userClearedToAll = canEscape && stored === '__all__';
        const currentValid = labList.some(l => l.lab_id === selectedLabId);
        // Always land on a real lab. Leaving it unset so the user "chooses
        // deliberately" reads, on screen, as a required picker that opens
        // empty — and a laboratory is changeable on every form it appears on,
        // so a default is a starting point, not a decision taken from them.
        if (!userClearedToAll && !currentValid) {
          const firstLab = labList[0].lab_id;
          setSelectedLabId(firstLab);
          localStorage.setItem('selectedLabId', firstLab);
        }
      } else {
        setSelectedLabId('');
        localStorage.removeItem('selectedLabId');
      }
    } catch {
      setLabs([]);
    }
  }, [selectedCompanyId, selectedLabId, currentUser, parentCompanies.length]);

  useEffect(() => { fetchCompanies().finally(() => setLoading(false)); }, [fetchCompanies]);
  useEffect(() => { if (selectedCompanyId) fetchLabs(); }, [selectedCompanyId, fetchLabs]);

  const selectCompany = (companyId) => {
    if (companyId === selectedCompanyId) return;
    // Write to localStorage FIRST, synchronously
    localStorage.setItem('selectedCompanyId', companyId);
    // A PERSON CHOSE THIS. The domain will not overrule it on the next
    // load — this is the only place that stamp is written, because this is
    // the only place a human actually decides.
    try { localStorage.setItem(EXPLICIT_KEY, companyId); } catch { /* private mode */ }
    localStorage.removeItem('selectedLabId');
    // Then update state
    setSelectedCompanyId(companyId);
    setSelectedLabId('');
    // Reload with enough delay for localStorage to be committed
    setTimeout(() => window.location.reload(), 300);
  };

  // Only admin / superadmin may escape lab scope. Tech / client / patient
  // users must always have a lab pinned (each lab is a separate CLIA license).
  const _canEscapeLabScope = () => {
    const role = currentUser?.role;
    return role === 'admin' || role === 'superadmin';
  };

  const selectLab = (labId) => {
    // Clicking the already-selected lab toggles it off so an admin can drop
    // back to "no lab" (cross-lab tenant view) or a superadmin can go fully
    // system-wide. For tech/client/patient roles, ignore the toggle and
    // keep the lab pinned.
    if (labId === selectedLabId) {
      if (!_canEscapeLabScope()) return;
      localStorage.setItem('selectedLabId', '__all__');
      setSelectedLabId('');
    } else {
      localStorage.setItem('selectedLabId', labId);
      setSelectedLabId(labId);
    }
    setTimeout(() => window.location.reload(), 300);
  };

  const clearLab = () => {
    if (!_canEscapeLabScope()) return;
    localStorage.setItem('selectedLabId', '__all__');
    setSelectedLabId('');
    setTimeout(() => window.location.reload(), 300);
  };

  const selectedCompany = parentCompanies.find(c => c.parent_company_id === selectedCompanyId) || null;
  const selectedLab = labs.find(l => l.lab_id === selectedLabId) || null;

  return (
    <LabContext.Provider value={{
      parentCompanies, labs, selectedCompanyId, selectedLabId,
      selectedCompany, selectedLab, isCorporateUser, tenantLocked, loading,
      selectCompany, selectLab, clearLab,
      refreshCompanies: fetchCompanies, refreshLabs: fetchLabs,
    }}>
      {children}
    </LabContext.Provider>
  );
}

export function useLabContext() {
  const ctx = useContext(LabContext);
  if (!ctx) return { parentCompanies: [], labs: [], selectedCompanyId: '', selectedLabId: '', isCorporateUser: false, loading: true, selectCompany: () => {}, selectLab: () => {} };
  return ctx;
}

export default LabContext;
